import { createAutoAnimate } from "@formkit/auto-animate/solid";
import { A, useNavigate, useLocation } from "@solidjs/router";
import {
  Accessor,
  JSX,
  Match,
  Setter,
  Show,
  Switch,
  createSignal,
} from "solid-js";
import {
  giftCardIcon,
  logoutIcon,
  profileIcon,
  rightArrowWhite,
  supportIcon,
} from "~/assets/assets";
import { Mode } from "~/types";
import { PhosphorIcon } from "~/widgets/icons";
import { AboutGiftCard } from "../about_gift_card";
import { useModal } from "../modal";
import { openIntercom } from "~/utils/third_party/intercom";
import { BusinessMenu } from "../business_menu";
import { classNames } from "~/utils/etc";
import { Spacer } from "~/widgets/spacer";
import { DividerV2 } from "~/widgets/divider";
import { clientRepo } from "~/server/apis/client_repo";
import {
  profileSvgIcon,
  giftCardSvgIcon,
  helpAndSupportSvgIcon,
  termsAndConditionsSvgIcon,
  logoutSvgIcon,
  profileSvgIconDark,
  giftCardSvgIconDark,
  termsAndConditionsSvgIconDark,
  helpAndSupportSvgIconDark,
  logoutSvgIconDark,
} from "../../assets/header_menu_svg_icons";
import { clientIdConfig, shouldDisableLogout } from "~/data/partner_config";
import PartnerFAQs from "../partner_components/faqs";
import { FAQ } from "~/data/static_faqs/types";
import { useOutsideClick } from "~/utils/hooks/use_outside_click";

export function HeaderMenu(props: {
  isLoggedIn: boolean;
  showBusinessMenu?: boolean;
  mode: Mode;
  clientId: () => string;
  showMyGiftCards?: Accessor<boolean | undefined>;
  partnerPortalTncUrl?: string;
  fAQsInHeader?: FAQ[];
  isDropdownOpen: Accessor<boolean>;
  setIsDropdownOpen: Setter<boolean>;
  headerButtonRef?: Accessor<HTMLDivElement | undefined>;
}) {
  const [parent] = createAutoAnimate();
  const [menuRef, setMenuRef] = createSignal<HTMLDivElement>();
  const [containerRef, setContainerRef] = createSignal<HTMLDivElement>();

  useOutsideClick(
    containerRef,
    () => {
      props.setIsDropdownOpen(false);
    },
    props.headerButtonRef
  );

  function getTopPositionBasedOnClientHeaderHeight() {
    if (props.clientId() === clientIdConfig.axisClientId) {
      return "top-[100px]";
    }

    if (props.clientId() === clientIdConfig.tdClientId) {
      return "top-[44px]";
    }

    return "top-11";
  }

  return (
    <div class="relative" ref={setContainerRef}>
      <div
        id="header-menu"
        class="shadow hidden h-10 w-10 cursor-pointer items-center justify-center gap-[9px] rounded-[34.20px] border border-baseTertiaryDark bg-baseTertiaryLight lg:inline-flex"
        tabIndex="0"
        onClick={(e) => {
          e.stopPropagation();
          props.setIsDropdownOpen(!props.isDropdownOpen());
        }}
      >
        <div class="block darkTheme:hidden">{profileSvgIcon()}</div>
        <div class="hidden darkTheme:block">{profileSvgIconDark()}</div>
      </div>

      <div
        class={classNames(
          `fixed left-0 mt-2 flex w-full flex-col bg-baseTertiaryLight sm:top-[12px]`,
          `md:absolute md:-left-[160px] md:top-[10px] md:w-[206px] `,
          getTopPositionBasedOnClientHeaderHeight()
        )}
        classList={{
          "md:rounded-xl md:border md:border-baseTertiaryDark":
            props.isDropdownOpen(),
        }}
        ref={setMenuRef}
      >
        <div ref={parent}>
          <Show when={props.isDropdownOpen()}>
            <DropdownMenu {...props} />
          </Show>
        </div>
      </div>
    </div>
  );

  function DropdownMenu(
    menuProps: { setIsDropdownOpen: Setter<boolean> } & Omit<
      typeof props,
      "isDropdownOpen" | "setIsDropdownOpen"
    >
  ) {
    const navigate = useNavigate();
    const location = useLocation();

    const { setIsModalOn, updateModalContentGetter } = useModal()!;

    const handleLogout = async () => {
      await clientRepo.logout();
      navigate("/logout");
    };

    const handleNavigation = (path: string) => {
      menuProps.setIsDropdownOpen(false);
      if (location.pathname === path) {
        // No navigation needed if already on the same path
      } else {
        navigate(path);
      }
    };

    return (
      <div
        class="flex flex-col p-2"
        classList={{
          "py-0 px-4":
            menuProps.clientId() === clientIdConfig.tdClientId ||
            menuProps.clientId() === clientIdConfig.bajajClientId,
        }}
      >
        <Show when={menuProps.showBusinessMenu}>
          <div class="md:hidden">
            <div class="truncate-[0.44px] pl-2 text-[11px] font-medium text-textNormal">
              FOR BUSINESS
            </div>
            <Spacer height={8} />
            <BusinessMenu />
            <Spacer height={8} />
            <DividerV2 class="bg-baseTertiaryMedium" />
            <Spacer height={8} />
          </div>
        </Show>
        <Switch>
          <Match when={!menuProps.isLoggedIn}>
            <div></div>
          </Match>
          <Match when={menuProps.showMyGiftCards?.() !== false}>
            <HeaderDropdownMenuItem
              title="My gift cards"
              icon="gift"
              iconUrl={giftCardIcon}
              iconBuilder={giftCardSvgIcon}
              darkIconBuilder={giftCardSvgIconDark}
              onClick={() => handleNavigation("/profile")}
              clientId={menuProps.clientId}
            />
          </Match>
        </Switch>
        <Show
          when={
            menuProps.partnerPortalTncUrl ||
            menuProps.clientId() === clientIdConfig.axisClientId
          }
        >
          <A
            href={menuProps.partnerPortalTncUrl ?? ""}
            class="select-none"
            onClick={() => menuProps.setIsDropdownOpen(false)}
          >
            <HeaderDropdownMenuItem
              title="Terms & conditions"
              icon="headset"
              iconUrl={supportIcon}
              iconBuilder={termsAndConditionsSvgIcon}
              darkIconBuilder={termsAndConditionsSvgIconDark}
              onClick={() => {}}
              clientId={menuProps.clientId}
            />
          </A>
        </Show>
        <Show when={(menuProps.fAQsInHeader ?? [])?.length > 0}>
          <HeaderDropdownMenuItem
            title="FAQs"
            icon="headset"
            iconUrl={supportIcon}
            iconBuilder={termsAndConditionsSvgIcon}
            darkIconBuilder={termsAndConditionsSvgIconDark}
            onClick={() => {
              updateModalContentGetter(() => (
                <PartnerFAQs
                  faqs={menuProps.fAQsInHeader ?? []}
                  onClickCloseIcon={() => {
                    setIsModalOn(false);
                  }}
                />
              ));
              setIsModalOn(true);
              menuProps.setIsDropdownOpen(false);
            }}
            clientId={menuProps.clientId}
          />
        </Show>
        {getSupportMenuItem()}
        <Show
          when={
            menuProps.isLoggedIn && !shouldDisableLogout(menuProps.clientId())
          }
        >
          <HeaderDropdownMenuItem
            title="Logout"
            icon="SignOut"
            iconUrl={logoutIcon}
            iconBuilder={logoutSvgIcon}
            darkIconBuilder={logoutSvgIconDark}
            onClick={handleLogout}
            clientId={() => menuProps.clientId()!}
          />
        </Show>
      </div>
    );

    function getSupportMenuItem() {
      return (
        <HeaderDropdownMenuItem
          title="Help & support"
          icon="headset"
          iconUrl={supportIcon}
          iconBuilder={helpAndSupportSvgIcon}
          darkIconBuilder={helpAndSupportSvgIconDark}
          onClick={() => {
            openIntercom();
            menuProps.setIsDropdownOpen(false);
          }}
          clientId={() => menuProps.clientId()!}
        />
      );
    }
  }
}

export function HeaderDropdownMenuItem(props: {
  title: string;
  subTitle?: string;
  icon: string;
  iconUrl?: string;
  onClick: () => void;
  iconBuilder?: () => JSX.Element;
  darkIconBuilder?: () => JSX.Element;
  clientId: () => string;
}) {
  return (
    <div
      class="inline-flex cursor-pointer select-none flex-row items-center justify-start self-stretch overflow-clip text-ellipsis py-2 hover:bg-baseTertiaryMedium"
      onClick={props.onClick}
      classList={{
        "h-14 pl-1 pr-2 w-full gap-2 border-b border-[#313538]":
          props.clientId() === clientIdConfig.tdClientId,
        "pl-1 pr-3 gap-1": props.clientId() !== clientIdConfig.tdClientId,
      }}
    >
      <Show when={props.clientId() !== clientIdConfig.tdClientId}>
        <div class="h-[24px] w-[24px] items-center justify-center fill-black darkTheme:fill-white">
          <Switch>
            <Match when={props.iconBuilder || props.darkIconBuilder}>
              <div class="block darkTheme:hidden">{props.iconBuilder?.()}</div>
              <div class="hidden darkTheme:block">
                {props.darkIconBuilder?.()}
              </div>
            </Match>
            <Match when={props.iconUrl}>
              <img src={props.iconUrl} />
            </Match>
            <Match when={true}>
              <PhosphorIcon
                name={props.icon}
                size="fill"
                fontSize={18}
                class="text-basePrimaryDark darkTheme:text-white"
              />
            </Match>
          </Switch>
        </div>
      </Show>
      <div class="flex flex-col gap-3">
        <span
          class="flex w-full select-none  text-subtitleSmall leading-[4px] text-textDark"
          classList={{
            "text-bold": props.clientId() === clientIdConfig.tdClientId,
          }}
        >
          {props.title}
        </span>
        <Show when={props.subTitle}>
          <span class="flex w-full select-none  text-small leading-[4px] text-textNormal">
            {props.subTitle}
          </span>
        </Show>
      </div>
      <Show when={props.clientId() === clientIdConfig.tdClientId}>
        <img src={rightArrowWhite} alt="" />
      </Show>
    </div>
  );
}
